import { Resort } from '@resort/context/ResortContext'
import { MK_RESORTS_PATH } from '@constants'

/**
 * Strips "PI|" prefix from productHubId for comparison purposes
 * This is used to normalize productHubId values between different resort data sources
 */
export const normalizeProductHubId = (productHubId: string | undefined): string => {
  if (!productHubId) return ''
  return productHubId.replace(/^PI\|/, '')
}

/**
 * Checks if a resort exists in a list of resorts based on productHubId comparison
 * Uses normalized productHubId for matching (strips "PI|" prefix)
 */
export const isResortInList = (resort: Resort, resortList: Resort[]): boolean => {
  const normalizedId = normalizeProductHubId(resort.productHubId)
  return resortList.some(listResort =>
    normalizeProductHubId(listResort.productHubId) === normalizedId
  )
}

/**
 * Filters resorts based on whether they exist in a reference list
 * Used for hiding non-brand resorts when the LD flag is enabled
 */
export const filterResortsByExistence = (
  resortsToFilter: Resort[],
  referenceList: Resort[],
  shouldHide: boolean = false
): Resort[] => {
  if (!shouldHide) {
    return resortsToFilter
  }

  return resortsToFilter.filter(resort => isResortInList(resort, referenceList))
}

/**
 * Generate a resort detail URL with location parameter
 * Format: /stays/resorts/{location}/{resortKey}
 *
 * @param resortKey - The resort's resource key (URL slug)
 * @param state - The state or territory name (e.g., "Utah", "Puerto Rico")
 * @returns The complete resort URL path
 *
 * @example
 * getResortUrl('club-wyndham-park-city', 'Utah')
 * // Returns: '/stays/resorts/utah/club-wyndham-park-city'
 */
export function getResortUrl(resortKey: string, state: string): string {
  const locationSlug = state.toLowerCase().replace(/\s+/g, '-')
  return `${MK_RESORTS_PATH}/${locationSlug}/${resortKey}`
}

/**
 * Get resort URL by resort key
 * Searches through all resorts to find a match by resourceKey and generates the appropriate URL
 *
 * @param resortKey - The resort's resource key (URL slug)
 * @param allResorts - Array of all available resorts
 * @returns The resort URL or fallback to resorts listing page if not found
 *
 * @example
 * getResortUrlByKey('club-wyndham-park-city', allResorts)
 * // Returns: '/stays/resorts/utah/club-wyndham-park-city'
 */
export function getResortUrlByKey(resortKey: string, allResorts: Resort[]): string {
  if (!resortKey) {
    return MK_RESORTS_PATH
  }

  // Find the resort by resourceKey
  const resort = allResorts.find((r) => r.resourceKey === resortKey)

  if (resort) {
    const state = resort.stateFull || resort.address?.stateFull || resort.locationCity?.state?.name || resort.state || resort.address?.state
    if (state) {
      return getResortUrl(resort.resourceKey, state)
    }
  }

  // Fallback to resorts listing page if resort not found
  console.warn(`Resort not found: ${resortKey}`)
  return MK_RESORTS_PATH
}

/**
 * Get resort URL by resort name
 * Searches through all resorts to find a match by name and generates the appropriate URL
 *
 * @param resortName - The name of the resort to find
 * @param allResorts - Array of all available resorts
 * @returns The resort URL or fallback to resorts listing page if not found
 *
 * @example
 * getResortUrlByName('Park City Resort', allResorts)
 * // Returns: '/stays/resorts/utah/club-wyndham-park-city'
 */
export function getResortUrlByName(resortName: string, allResorts: Resort[]): string {
  if (!resortName) {
    return MK_RESORTS_PATH
  }

  // Find the resort by name (case-insensitive match)
  const resort = allResorts.find(
    (r) => r.name?.toLowerCase() === resortName.toLowerCase() || r.prodName?.toLowerCase() === resortName.toLowerCase()
  )

  if (resort && resort.resourceKey) {
    const state = resort.stateFull || resort.address?.stateFull || resort.locationCity?.state?.name || resort.state || resort.address?.state
    if (state) {
      return getResortUrl(resort.resourceKey, state)
    }
  }

  // Fallback to resorts listing page if resort not found
  console.warn(`Resort not found: ${resortName}`)
  return MK_RESORTS_PATH
}
